import { lazy, Suspense, useEffect, useRef } from "react";
import Hero from "./Hero";
import PopperLine from "./PopperLine";
import ErrorBoundary from "./ErrorBoundary";
import { copy } from "../content/copy";
import { openExperiences } from "../experiences/registry";
import { Link } from "../lib/router";
import { initReveals } from "../lib/reveal";

const Playground = lazy(() => import("../experiences/playground/Playground"));

export default function Home() {
  const open = openExperiences();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const cleanSections = initReveals(main, ".section > *:not(.card-grid)");
    const cleanCards = initReveals(main, ".card", 80);

    // One delegated listener feeds every card's spotlight (--mx/--my in CSS).
    const onMove = (e: PointerEvent) => {
      const card = (e.target as Element).closest?.(".card");
      if (!(card instanceof HTMLElement)) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty("--mx", `${e.clientX - r.left}px`);
      card.style.setProperty("--my", `${e.clientY - r.top}px`);
    };
    main.addEventListener("pointermove", onMove);

    return () => {
      cleanSections();
      cleanCards();
      main.removeEventListener("pointermove", onMove);
    };
  }, []);

  return (
    <main ref={mainRef}>
      <Hero />

      <section className="section" id="playground">
        <p className="kicker">{copy.playground.kicker}</p>
        <h2>{copy.playground.title}</h2>
        <p className="section-body">{copy.playground.body}</p>
        <div className="stage" aria-label="3D balloon playground">
          <ErrorBoundary label="playground">
            <Suspense fallback={<p className="stage-loading">{copy.playground.loading}</p>}>
              <Playground />
            </Suspense>
          </ErrorBoundary>
        </div>
      </section>

      <section className="section" id="lab">
        <p className="kicker">{copy.lab.kicker}</p>
        <h2>{copy.lab.title}</h2>
        <p className="section-body">{copy.lab.body}</p>
        <div className="card-grid">
          {open.map((e) => (
            <Link key={e.id} to={e.path} className={`card card-${e.id}`}>
              <span className="badge">{e.badge}</span>
              <h3>{e.title}</h3>
              <p>{e.blurb}</p>
            </Link>
          ))}
          <article className="card card-soon">
            <span className="badge badge-soon">{copy.lab.items.soon.badge}</span>
            <h3>{copy.lab.items.soon.title}</h3>
            <p>{copy.lab.items.soon.blurb}</p>
          </article>
        </div>
      </section>

      <section className="section" id="about">
        <p className="kicker">{copy.about.kicker}</p>
        <h2>{copy.about.title}</h2>
        <p className="section-body">{copy.about.body}</p>
      </section>

      <PopperLine />
    </main>
  );
}
